import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { AanAfwezigheid } from 'src/app/afas-classes/aanafwezigheid.class';
import { Planning } from 'src/app/classes/planning.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { CodaltComponent } from 'src/app/codalt.component';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';

interface WeekSchedulePlanningItem {
    begindate: Date;
    enddate: Date;
    planning?: Planning;
    comment?: string;
    dayOff?: boolean;
    dayOffReason?: string;
    hourtype?: RealisationHourtype;
}

@Component({
    selector: 'app-week-schedule-planning',
    templateUrl: './week-schedule-planning.component.html',
    standalone: false,
})
export class WeekSchedulePlanningComponent extends CodaltComponent implements OnChanges {
    @Input() beginDate: Date;
    @Input() endDate: Date;
    @Input() maxDate: Date;
    @Input() realisations: Realisation[];
    @Input() aanAfwezigheid: AanAfwezigheid[];

    @Output() openPlanning = new EventEmitter<WeekSchedulePlanningItem>();

    planningItems: WeekSchedulePlanningItem[] = [];

    constructor(private planningService: PlanningService) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.beginDate || changes.endDate) {
            this.loadData();
        }
    }

    private loadData() {
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();

        const toDate = new Date(this.endDate);
        toDate.setDate(toDate.getDate() + 1);

        this.subscriptions.add(
            this.planningService.getFilteredList(this.beginDate, toDate).subscribe(plannings => {
                const userId = LocalStorage.getUser().id;
                this.planningItems = plannings.flatMap(planning => {
                    const planningItems: WeekSchedulePlanningItem[] = [
                        ...(planning.user_planning ?? []).filter(userPlanning => userPlanning.user_id === userId),
                        ...(planning.planning_has ?? []).filter(planningHas => planningHas.driver_user_id === userId),
                    ].map(planningItem => ({
                        begindate: new Date(planningItem.begindate),
                        enddate: new Date(planningItem.enddate),
                        planning,
                        comment: planningItem.comment,
                    }));

                    if (planning.performer === userId && planning.mainPlanning && planningItems.length === 0) {
                        planningItems.push({
                            begindate: new Date(planning.mainPlanning.begindate),
                            enddate: new Date(planning.mainPlanning.enddate),
                            planning,
                        });
                    }

                    return planningItems;
                });
                this.planningItems.push(
                    ...this.realisations
                        .filter(
                            realisation =>
                                !!realisation.leave_id &&
                                Utils.getTimeOrNull(realisation.begindate) < this.maxDate.getTime()
                        )
                        .map(realisation => ({
                            begindate: new Date(realisation.begindate),
                            enddate: new Date(realisation.enddate),
                            comment: realisation.leave?.comment,
                            dayOff: true,
                            hourtype: realisation.hourtype,
                        }))
                );

                for (let i = 0; i <= 6; i++) {
                    const loopDay = Utils.setTime(new Date(this.beginDate), 0, 0);
                    loopDay.setDate(loopDay.getDate() + i);
                    if (
                        !this.planningItems.some(item => Utils.dateString(item.begindate) === Utils.dateString(loopDay))
                    ) {
                        this.planningItems.push({
                            dayOff: true,
                            begindate: loopDay,
                            enddate: Utils.setTimeNewDate(loopDay, 23, 59, 59),
                        });
                    }
                }

                this.aanAfwezigheid.forEach(aanAfwezig => {
                    const planningItem = this.planningItems.find(
                        item =>
                            item.dayOff && Utils.dateString(item.begindate) === Utils.dateString(aanAfwezig.startDate)
                    );
                    if (planningItem) {
                        planningItem.dayOffReason = Utils.minutes(aanAfwezig.hours) + ' - ' + aanAfwezig.omschrijving;
                    }
                });

                this.planningItems.sort(
                    (a, b) =>
                        Utils.getTimeOrNull(a.dayOff ? a.enddate : a.begindate) -
                        Utils.getTimeOrNull(b.dayOff ? b.enddate : b.begindate)
                );
            })
        );
    }
}
